/** @jsx jsx */
// External modules
import { Fragment } from 'react';
import { jsx } from '@emotion/core';
import { string } from 'prop-types';
import Loadable from 'react-loadable';
import { LoadingElement, LoadingSpinner } from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { onboardAccount } from './handlers';
import { fptiTrackPageLoad } from '@/lib/logger';
import { returnAfterSubmitStyles } from './styles';
import { addressHeaderStyles } from '../Address/styles';
import { SectionHeading, SectionHeadingLarge } from '../Headings';
import {
  AddressLoadingContainer,
  SignupValueProp,
  SignupValuePropBold,
} from './StyledComponents';
import { LoadingFields, StyledLink } from '@/components/Common';
import {
  $logger,
  buildMPPUrl,
  features,
  getContent,
  getLazyLoaderRenderFunction,
  getLocale,
  lazyLoader,
} from '@/lib';
import { FRS_PATH_MAP } from './constants';

if (features.isNotActive('isAddressless')) {
  fptiTrackPageLoad.registerItem('Address');
}

const AccountSectionHeader = () => {
  const isGuestEligible = features.isActive('isGuestEligible');
  const flow = isGuestEligible ? 'guest' : 'signup';
  const subFlow = features.isActive('hasFreeReturnShipping') ? 'frs' : 'base';

  /**
   * Adding `isGuestEligible` to this check for the time being since
   * address-less is on in sign up flows (IN). If we expand to guest
   * eligible countries we should remove this if the design is updated
   * for guest flows.
   */
  if (!isGuestEligible && features.isActive('isAddressless')) {
    return (
      <SectionHeading>
        {getContent(`sectionHeaders.accountInfo.${flow}.addressless`)}
      </SectionHeading>
    );
  }

  return (
    <Fragment>
      <SectionHeadingLarge>
        {getContent(`sectionHeaders.accountInfo.${flow}.${subFlow}`, {
          brTag: <br />,
        })}
      </SectionHeadingLarge>

      <SignupValueProp>
        {getContent(
          `valueProps.accountInfo.${flow}.${subFlow}`,
          getValuePropComponents(),
        )}
      </SignupValueProp>

      {isGuestEligible && (
        <SignupValuePropBold>
          {getContent(`sectionHeaders.accountInfo.${flow}.subheader`)}
        </SignupValuePropBold>
      )}
    </Fragment>
  );
};

const MppLink = ({ contentKey, path }) => (
  <StyledLink
    href={buildMPPUrl({ mppPath: path })}
    rel="noopener noreferrer"
    target="_blank"
  >
    {getContent(contentKey)}
  </StyledLink>
);

MppLink.propTypes = {
  contentKey: string.isRequired,
  path: string.isRequired,
};

/**
 * Please name content keys for value props based on:
 * (a) The name of the link if they are a link
 * (b) The name of the content if is bolded content
 *
 * Also, please make sure any new content key is first added to the
 * `Base/en/weasley/valueProps.properties` file. This will prevent any
 * content missing logs for countries that don't use that content key.
 * Generally we like to avoid adding content to countries that don't use it
 * in order to prevent bloat of content files, but this is an exception that
 * is necessary to prevent noise in logs we use for finding missing content.
 */
const getValuePropComponents = () => {
  const flow = features.isActive('isGuestEligible') ? 'guest' : 'signup';
  const { country } = getLocale();

  return {
    buyerProtection: (
      <span css={{ fontWeight: '600' }}>
        {getContent(`valueProps.buyerProtection.${flow}`)}
      </span>
    ),
    buyerProtectionLink: (
      <MppLink
        contentKey={`valueProps.buyerProtectionLink.${flow}`}
        path="paypal-buyer-protection"
      />
    ),
    onlineShoppingLink: (
      <MppLink
        contentKey={`valueProps.onlineShoppingLink.${flow}`}
        path="online-shopping"
      />
    ),
    returnShippingTermsLink: (
      <MppLink
        contentKey={'valueProps.returnShippingTermsLink'}
        path={FRS_PATH_MAP[country]}
      />
    ),
    safetyAndSecurityLink: (
      <MppLink
        contentKey={`valueProps.safetyAndSecurityLink.${flow}`}
        path="paypal-safety-and-security"
      />
    ),
  };
};

const LazyAddress = lazyLoader({
  loader: () => import('../Address'),
  loading: () => (
    <AddressLoadingContainer>
      <p css={addressHeaderStyles}>{getContent('billing.billingAddress')}</p>
      <LoadingFields numLoaders={6} />
      <LoadingElement height="25px" viewBoxHeight="25" />
    </AddressLoadingContainer>
  ),
  name: 'address_input',
  render: getLazyLoaderRenderFunction({ componentName: 'Address' }),
});

const ReturnAfterSubmitMessage = () => (
  <p css={returnAfterSubmitStyles}>
    {getContent('billing.returnAfterSubmitMessage')}
  </p>
);

const LazyPasswordlessOnboarding = lazyLoader({
  loader: () => import('@/components/PasswordlessOnboarding'),
  loading: () => <LoadingElement />,
  name: 'passwordless_onboarding',
  render(loaded, props) {
    const Component = loaded.PasswordlessOnboarding;
    return <Component {...props} />;
  },
});

const LazyAddressNormalizedAddressInterstitial = Loadable({
  loader: () => import('../Interstitials/AddressNormalizationInterstitial'),
  loading: props => {
    const { ui: uiActions } = appStore.getActions();
    if (props.error) {
      $logger.warn('address_normalization_bundle_fetch_failed');
      uiActions.toggleIsAddressNormalizeOpen(false);
      onboardAccount();
    }
    return null;
  },
  render(loaded, props) {
    const Component = loaded.AddressNormalizationInterstitial;
    return <Component />;
  },
});

const LazyInstallmentInterstitial = Loadable({
  loader: () => import('@/components/Interstitials/InstallmentInterstitial'),
  loading: () => <LoadingSpinner />,
  render(loaded, props) {
    const Component = loaded.InstallmentInterstitial;
    return <Component {...props} />;
  },
});

const LazyThreeDsInterstitial = lazyLoader({
  loader: () => import('@/components/Interstitials/ThreeDsInterstitial'),
  loading: () => (
    <LoadingSpinner message={getContent('spinner.contactingCardIssuer')} />
  ),
  name: 'three_ds_interstitial',
  render(loaded, props) {
    const Component = loaded.ThreeDsInterstitial;
    return <Component />;
  },
});

const LazySepaInterstitial = lazyLoader({
  loader: () => import('@/components/Interstitials/SepaInterstitial'),
  loading: () => <LoadingSpinner />,
  name: 'sepa_interstitial',
  render(loaded, props) {
    const Component = loaded.SepaInterstitial;
    return <Component />;
  },
});

const LazyPhoneConfirmationInterstitial = lazyLoader({
  loader: () =>
    import('@/components/Interstitials/PhoneConfirmationInterstitial'),
  loading: () => <LoadingSpinner />,
  name: 'phone_confirmation_interstitial',
  render(loaded, props) {
    const Component = loaded.PhoneConfirmationInterstitial;
    return <Component />;
  },
});

const LazyOneTimePasswordConfirmationInterstitial = lazyLoader({
  loader: () =>
    import(
      '@/components/Interstitials/OneTimePasswordConfirmationInterstitial'
    ),
  loading: () => <LoadingSpinner />,
  name: 'one_time_password_confirmation_interstitial',
  render(loaded, props) {
    const Component = loaded.OneTimePasswordConfirmationInterstitial;
    return <Component />;
  },
});

const LazyShipping = lazyLoader({
  loader: () => import('../Shipping'),
  loading: () => <LoadingSpinner />,
  name: 'shipping_methods',
  render(loaded, props) {
    const Component = loaded.Shipping;
    return <Component />;
  },
});

export {
  AccountSectionHeader,
  getValuePropComponents,
  LazyAddress,
  LazyAddressNormalizedAddressInterstitial,
  LazyInstallmentInterstitial,
  LazyOneTimePasswordConfirmationInterstitial,
  LazyPasswordlessOnboarding,
  LazyPhoneConfirmationInterstitial,
  LazySepaInterstitial,
  LazyShipping,
  LazyThreeDsInterstitial,
  ReturnAfterSubmitMessage,
};
